/**
 * Payment domain events.
 * Tracked in: apps/api, apps/pos, apps/functions
 *
 * Value: Payment success/failure rates, processor reliability,
 * revenue tracking, refund patterns, tip analytics.
 */

import { defineEvent } from './types';

export const paymentInitiated = defineEvent<'payment_initiated', {
    tenantId: string;
    restaurantId: string;
    orderId: string;
    checkId?: string;
    amount: number;
    method: 'card_present' | 'card_not_present' | 'cash' | 'gift_card' | 'split';
    processor: string;
}>('payment_initiated');

export const paymentCaptured = defineEvent<'payment_captured', {
    tenantId: string;
    restaurantId: string;
    orderId: string;
    checkId?: string;
    transactionId: string;
    amount: number;
    method: string;
    processor: string;
    latencyMs: number;
}>('payment_captured');

export const paymentFailed = defineEvent<'payment_failed', {
    tenantId: string;
    restaurantId: string;
    orderId: string;
    checkId?: string;
    amount: number;
    method: string;
    processor: string;
    errorCode: string;
    errorMessage: string;
    isRetry: boolean;
}>('payment_failed');

export const refundIssued = defineEvent<'refund_issued', {
    tenantId: string;
    restaurantId: string;
    orderId: string;
    transactionId: string;
    refundAmount: number;
    originalAmount: number;
    reason: string;
    isPartial: boolean;
}>('refund_issued');

export const tipAdded = defineEvent<'tip_added', {
    tenantId: string;
    restaurantId: string;
    orderId: string;
    tipAmount: number;
    tipPercent: number;
    orderTotal: number;
    source: 'pos' | 'online' | 'adjustment';
}>('tip_added');

// ─── Cash drawer ──────────────────────────────────────────────────────────
// Every event below carries drawerGuid + sessionId. Without them these are
// store-wide counters, and the loss-prevention questions they exist to answer
// ("which till, which shift, whose drawer") are unanswerable.

export const cashDrawerOpened = defineEvent<'cash_drawer_opened', {
    tenantId: string;
    restaurantId: string;
    reason: 'sale' | 'no_sale' | 'pay_in' | 'pay_out' | 'close_out' | 'manual';
    drawerGuid: string;
    sessionId?: string;
    printerId?: number;
    employeeId?: string;
}>('cash_drawer_opened');

export const cashDrawerSessionOpened = defineEvent<'cash_drawer_session_opened', {
    tenantId: string;
    restaurantId: string;
    sessionId: string;
    drawerGuid: string;
    openingFloatCents: number;
    /** Whether the opening float was physically counted rather than assumed. */
    counted: boolean;
    assignmentMode: 'shared' | 'assigned';
    employeeId?: string;
}>('cash_drawer_session_opened');

export const cashDrawerSessionClosed = defineEvent<'cash_drawer_session_closed', {
    tenantId: string;
    restaurantId: string;
    sessionId: string;
    drawerGuid: string;
    expectedTotalCents: number;
    countedTotalCents: number;
    /** counted − expected. Positive = over, negative = short. */
    varianceCents: number;
    /** Counter could not see the expected total. An over/short from a blind
     *  count is evidence; one from an open count is not. */
    blind: boolean;
    approved: boolean;
    employeeId?: string;
    approvedByEmployeeId?: string;
}>('cash_drawer_session_closed');

export const cashDrawerEventRecorded = defineEvent<'cash_drawer_event_recorded', {
    tenantId: string;
    restaurantId: string;
    sessionId?: string;
    drawerGuid?: string;
    type: 'deposit' | 'withdrawal' | 'float' | 'no_sale' | 'variance';
    reason: string;
    amountCents: number;
    employeeId?: string;
}>('cash_drawer_event_recorded');

export const cashDrawerLockdownTriggered = defineEvent<'cash_drawer_lockdown_triggered', {
    tenantId: string;
    restaurantId: string;
    sessionId: string;
    drawerGuid: string;
    expectedTotalCents: number;
    maxCashCents: number;
    /** True when a manager overrode the ceiling instead of dropping cash. */
    overridden: boolean;
    employeeId?: string;
}>('cash_drawer_lockdown_triggered');
